import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Camera, Keyboard, Check, X, ScanLine, AlertTriangle, Shield } from 'lucide-react';

// AAMVA PDF417 parser - extracts DOB from US driver's license barcode
function parseAAMVABarcode(raw) {
  try {
    const data = raw.replace(/\r/g, '\n');
    // Look for DAQ (ID number), DBB (DOB), DCS (last name), DAC (first name)
    const dobMatch = data.match(/DBB(\d{8})/);
    const firstNameMatch = data.match(/DAC([^\n]+)/);
    const lastNameMatch = data.match(/DCS([^\n]+)/);
    const idMatch = data.match(/DAQ([^\n]+)/);

    if (dobMatch) {
      const raw = dobMatch[1]; // MMDDYYYY format
      const mm = raw.substring(0, 2);
      const dd = raw.substring(2, 4);
      const yyyy = raw.substring(4, 8);
      return {
        dob: `${yyyy}-${mm}-${dd}`,
        firstName: firstNameMatch ? firstNameMatch[1].trim() : '',
        lastName: lastNameMatch ? lastNameMatch[1].trim() : '',
        idNumber: idMatch ? idMatch[1].trim().slice(-4) : '',
        parsed: true
      };
    }
    return { parsed: false };
  } catch {
    return { parsed: false };
  }
}

function calculateAge(dob) {
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

export default function AgeVerificationDialog({ open, onClose, onVerified, onDenied, productName }) {
  const [mode, setMode] = useState('manual'); // manual, camera, scanner
  const [idType, setIdType] = useState('drivers_license');
  const [dob, setDob] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [cameraError, setCameraError] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scannerInputRef = useRef(null);
  const scanBufferRef = useRef('');
  const scanTimerRef = useRef(null);

  // Cleanup camera on unmount or mode change
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setScanning(true);

      // Try BarcodeDetector API (Chrome 83+)
      if ('BarcodeDetector' in window) {
        const detector = new window.BarcodeDetector({ formats: ['pdf417', 'code_128', 'code_39'] });
        const detectLoop = async () => {
          if (!videoRef.current || !streamRef.current) return;
          try {
            const barcodes = await detector.detect(videoRef.current);
            if (barcodes.length > 0) {
              const parsed = parseAAMVABarcode(barcodes[0].rawValue);
              if (parsed.parsed) {
                handleScanResult(parsed);
                stopCamera();
                return;
              }
            }
          } catch {}
          if (streamRef.current) {
            requestAnimationFrame(detectLoop);
          }
        };
        // Start detection after video is playing
        videoRef.current.onplaying = () => {
          detectLoop();
        };
      }
    } catch (err) {
      setCameraError('Camera access denied. Please allow camera access in your browser settings.');
      console.error('Camera error:', err);
    }
  };

  const handleScanResult = (parsed) => {
    setScanResult(parsed);
    setDob(parsed.dob);
    setIdNumber(parsed.idNumber);
    if (parsed.firstName || parsed.lastName) {
      setCustomerName(`${parsed.firstName} ${parsed.lastName}`.trim());
    }
  };

  // USB Barcode Scanner handler - captures rapid keyboard input
  const handleScannerKeyDown = useCallback((e) => {
    if (mode !== 'scanner') return;

    // Barcode scanners send characters rapidly followed by Enter
    if (e.key === 'Enter') {
      e.preventDefault();
      const buffer = scanBufferRef.current;
      if (buffer.length > 10) {
        const parsed = parseAAMVABarcode(buffer);
        if (parsed.parsed) {
          handleScanResult(parsed);
        } else {
          // Try treating as raw text with DOB pattern
          const dobPattern = buffer.match(/(\d{2})[/-](\d{2})[/-](\d{4})/);
          if (dobPattern) {
            setDob(`${dobPattern[3]}-${dobPattern[1]}-${dobPattern[2]}`);
          }
        }
      }
      scanBufferRef.current = '';
      return;
    }

    // Accumulate characters
    if (e.key.length === 1) {
      scanBufferRef.current += e.key;
      // Auto-clear buffer if no input for 100ms (not from scanner)
      clearTimeout(scanTimerRef.current);
      scanTimerRef.current = setTimeout(() => {
        scanBufferRef.current = '';
      }, 100);
    }
  }, [mode]);

  useEffect(() => {
    if (mode === 'scanner') {
      window.addEventListener('keydown', handleScannerKeyDown);
      return () => window.removeEventListener('keydown', handleScannerKeyDown);
    }
  }, [mode, handleScannerKeyDown]);

  // Focus scanner input when in scanner mode
  useEffect(() => {
    if (mode === 'scanner' && scannerInputRef.current) {
      scannerInputRef.current.focus();
    }
  }, [mode]);

  const handleVerify = () => {
    if (!dob) return;
    const age = calculateAge(dob);
    if (age >= 21) {
      onVerified({
        dob,
        age,
        idType,
        idNumber,
        customerName,
        method: mode,
        verifiedAt: new Date().toISOString()
      });
    } else {
      onDenied({ dob, age, reason: 'Under 21' });
    }
  };

  const handleManualOverride = () => {
    onVerified({
      dob: dob || 'manual_override',
      age: 21,
      idType: 'manual_override',
      idNumber: '',
      customerName: '',
      method: 'manual_override',
      verifiedAt: new Date().toISOString()
    });
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  const age = dob ? calculateAge(dob) : null;
  const isValid = age !== null && age >= 21;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="age-verify-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-amber-600">
            <Shield className="h-6 w-6" /> 21+ Age Verification Required
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Product Info */}
          {productName && (
            <div className="p-3 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-700 rounded-lg">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                <AlertTriangle className="inline w-4 h-4 mr-1" />
                Age-restricted product: <strong>{productName}</strong>
              </p>
            </div>
          )}

          {/* Mode Selector */}
          <div className="flex gap-2" data-testid="scan-mode-selector">
            <button
              onClick={() => { stopCamera(); setMode('manual'); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-colors border ${
                mode === 'manual'
                  ? 'bg-amber-600 text-white border-amber-600'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
              data-testid="mode-manual-btn"
            >
              <Keyboard className="w-4 h-4" /> Manual DOB
            </button>
            <button
              onClick={() => { stopCamera(); setMode('camera'); startCamera(); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-colors border ${
                mode === 'camera'
                  ? 'bg-amber-600 text-white border-amber-600'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
              data-testid="mode-camera-btn"
            >
              <Camera className="w-4 h-4" /> Camera Scan
            </button>
            <button
              onClick={() => { stopCamera(); setMode('scanner'); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-colors border ${
                mode === 'scanner'
                  ? 'bg-amber-600 text-white border-amber-600'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
              data-testid="mode-scanner-btn"
            >
              <ScanLine className="w-4 h-4" /> USB Scanner
            </button>
          </div>

          {/* Camera Mode */}
          {mode === 'camera' && (
            <div className="space-y-3">
              <div className="relative bg-black rounded-lg overflow-hidden aspect-video" data-testid="camera-view">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                {scanning && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-3/4 h-1/3 border-2 border-amber-500 rounded-lg opacity-70">
                      <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-amber-500 animate-pulse" />
                    </div>
                  </div>
                )}
                {!scanning && !cameraError && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-white/60 text-sm">Starting camera...</p>
                  </div>
                )}
              </div>
              {cameraError && (
                <p className="text-sm text-red-500">{cameraError}</p>
              )}
              <p className="text-xs text-slate-500 text-center">
                Hold the back of the ID (barcode side) in front of the camera
              </p>
            </div>
          )}

          {/* USB Scanner Mode */}
          {mode === 'scanner' && (
            <div className="space-y-3" data-testid="scanner-mode">
              <div className="p-6 bg-slate-100 dark:bg-slate-800/50 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-center">
                <ScanLine className="w-10 h-10 text-amber-500 mx-auto mb-2 animate-pulse" />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  Ready to scan...
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Scan the PDF417 barcode on the back of the ID
                </p>
                <input
                  ref={scannerInputRef}
                  type="text"
                  className="sr-only"
                  aria-label="Barcode scanner input"
                  data-testid="scanner-input"
                />
              </div>
              {scanResult && scanResult.parsed && (
                <div className="p-3 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-700 rounded-lg">
                  <p className="text-sm text-emerald-700 dark:text-emerald-300 font-medium">
                    <Check className="inline w-4 h-4 mr-1" /> ID Scanned Successfully
                  </p>
                  {customerName && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">Name: {customerName}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Manual / Common Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">ID Type</Label>
              <Select value={idType} onValueChange={setIdType}>
                <SelectTrigger data-testid="id-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="drivers_license">Driver's License</SelectItem>
                  <SelectItem value="state_id">State ID</SelectItem>
                  <SelectItem value="passport">Passport</SelectItem>
                  <SelectItem value="military_id">Military ID</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">ID # (last 4)</Label>
              <Input
                type="text"
                placeholder="Last 4 digits"
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value)}
                maxLength={4}
                data-testid="id-number-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Date of Birth {mode === 'manual' ? '(required)' : '(auto-filled from scan)'}</Label>
            <Input
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="h-12 text-lg"
              max={new Date().toISOString().split('T')[0]}
              data-testid="dob-input"
            />
          </div>

          {/* Age Result */}
          {age !== null && (
            <div className={`p-3 rounded-lg border text-center ${
              isValid
                ? 'bg-emerald-50 dark:bg-emerald-950/50 border-emerald-200 dark:border-emerald-700'
                : 'bg-red-50 dark:bg-red-950/50 border-red-200 dark:border-red-700'
            }`} data-testid="age-result">
              <p className={`font-bold text-lg ${isValid ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                {isValid ? <Check className="inline w-5 h-5 mr-1" /> : <X className="inline w-5 h-5 mr-1" />}
                Age: {age} years old
                {isValid ? ' — APPROVED' : ' — DENIED (Must be 21+)'}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button variant="outline" onClick={handleClose} data-testid="cancel-verify-btn">
            Cancel Sale
          </Button>
          <Button
            variant="ghost"
            onClick={handleManualOverride}
            className="text-xs text-slate-500"
            data-testid="manual-override-btn"
          >
            Manager Override
          </Button>
          <Button
            onClick={handleVerify}
            disabled={!dob || (age !== null && age < 21)}
            className={isValid ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'}
            data-testid="verify-age-btn"
          >
            <Check className="h-4 w-4 mr-2" />
            {isValid ? 'Approve & Continue' : 'Verify Age'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
